import { ErrorIcon } from "evergreen-ui";
import React, { useCallback, useEffect, useState } from "react";
import { generatePath, useHistory, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { CenteredSpinner } from "../../common/CenteredSpinner";
import { EmptyState } from "../../common/EmptyState";
import { MultiTabView } from "../../common/multitab/MultiTabView";
import { TabInfo } from "../../common/multitab/TabInfo";
import { useProductListingQuery } from "../../datastore/ProductListingDataStore";
import { queryCostMetadataByShopifyProductId } from "../../datastore/CostMetadataDataStore";
import { queryShopifyProductByProductListingId } from "../../datastore/ShopifyProductDataStore";
import { NavigationHelper } from "../../navigation/PrevLocationState";
import { CostMetadataTab } from "./CostMetadataTab";
import { PayoutRuleMappingTab } from "./PayoutRuleMappingTab";
import { ProductDetailsTab } from "./ProductDetailsTab";
import { ProductVariantsTab } from "./ProductVariantsTab";
import { RampartAuthorized } from "../../rampart/RampartAuthorized";

type Params = { productListingId: string; tabId?: ProductTabIds };

const PRODUCT_ROUTE_PATH = "/products/:productListingId/:tabId?";

const EDIT_IN_SHOPIFY_BASE_URL = "https://admin.shopify.com/store/";

enum ProductTabIds {
    details = "details",
    productVariants = "product-variants",
    costMetadata = "cost-metadata",
    payoutRules = "payout-rules",
    editInShopify = "edit-in-shopify",
}

const MultiTabProductPageInternal = () => {

    const history = useHistory();
    const { productListingId, tabId } = useParams<Params>();

    const resolveSelectedTabIndex = useCallback(
        (tab?: ProductTabIds): number => {
            switch (tab) {
                case ProductTabIds.details:
                    return 0;
                case ProductTabIds.productVariants:
                    return 1;
                case ProductTabIds.costMetadata:
                    return 2;
                case ProductTabIds.payoutRules:
                    return 3;
                default:
                    return 0;
            }
        },
        []
    );

    const [selectedTabIndex, setSelectedTabIndex] = useState<number | null>(null);
    const [shopifyAdminUrl, setShopifyAdminUrl] = useState<string | null>(null);
    const [hasPayoutRuleAssignments, setHasPayoutRuleAssignments] = useState<boolean>(true);
    const [hasCostMetadata, setHasCostMetadata] = useState<boolean>(true);

    const {
        loading: productLoading,
        error: productError,
        data: productData,
    } = useProductListingQuery(productListingId);

    // TODO: shopifyAccountId should live on ShopifyProduct, not CostMetadata.
    // A ShopifyProduct is ingested from exactly one Shopify store, but the current
    // schema places shopifyAccountId on CostMetadata — meaning multiple records for
    // the same product could theoretically have different values. In practice this
    // doesn't happen, but if it did, we'd silently use whichever record is returned
    // first and potentially link to the wrong Shopify store. The correct fix is to
    // add a shopifyAccountId column to ShopifyProduct (or derive it from the Hevo
    // ingestion source) and read it directly from there.
    useEffect(() => {
        if (!productData?.shopifyProductId) {
            return;
        }
        queryCostMetadataByShopifyProductId({
            shopifyProductId: productData.shopifyProductId,
            options: { take: 1 },
        }).then((costMetadata) => {
            setHasCostMetadata(costMetadata.length > 0);
            if (costMetadata.length > 0 && costMetadata[0].shopifyAccountId) {
                const domainPrefix = costMetadata[0].shopifyAccountId;
                setShopifyAdminUrl(
                    `${EDIT_IN_SHOPIFY_BASE_URL}${domainPrefix}/products/${productData.shopifyProductId}`
                );
            }
        }).catch((error) => {
            Sentry.captureException(error);
        });
    }, [productData?.shopifyProductId]);

    // Query hasPayoutRuleAssignments eagerly on page load for the tab ErrorIcon
    useEffect(() => {
        if (!productListingId) {
            return;
        }
        queryShopifyProductByProductListingId(productListingId)
            .then((shopifyProduct) => {
                const product = shopifyProduct as (typeof shopifyProduct & { hasPayoutRuleAssignments?: boolean }) | null;
                setHasPayoutRuleAssignments(product?.hasPayoutRuleAssignments ?? false);
            })
            .catch((error) => {
                Sentry.captureException(error);
            });
    }, [productListingId]);

    const onCostMetadataDataLoaded = useCallback((hasData: boolean) => {
        setHasCostMetadata(hasData);
    }, []);

    const onPayoutRuleDataLoaded = useCallback((hasData: boolean) => {
        setHasPayoutRuleAssignments(hasData);
    }, []);

    const getTabs = useCallback((): TabInfo[] => {
        const result: TabInfo[] = [];

        result.push({
            label: "Details",
            tabId: ProductTabIds.details,
            locked: false,
            component: (
                <ProductDetailsTab
                    productListingId={productListingId}
                    mainSku={productData?.mainSku}
                    storeId={productData?.store?.id}
                    storeName={productData?.store?.name}
                />
            ),
        });

        result.push({
            label: "Product Variants",
            tabId: ProductTabIds.productVariants,
            locked: false,
            component: <ProductVariantsTab productListingId={productListingId} shopifyProductId={productData?.shopifyProductId ?? undefined} />,
        });

        result.push({
            label: "Cost Metadata",
            tabId: ProductTabIds.costMetadata,
            locked: false,
            component: <CostMetadataTab shopifyProductId={productData?.shopifyProductId} productListingId={productListingId} onDataLoaded={onCostMetadataDataLoaded} />,
            icon: !hasCostMetadata ? <ErrorIcon size={12} color="danger" /> : undefined,
        });

        result.push({
            label: "Payout Rules",
            tabId: ProductTabIds.payoutRules,
            locked: false,
            component: <PayoutRuleMappingTab productListingId={productListingId} storeId={productData?.store?.id} onDataLoaded={onPayoutRuleDataLoaded} />,
            icon: !hasPayoutRuleAssignments ? <ErrorIcon size={12} color="danger" /> : undefined,
        });

        if (shopifyAdminUrl) {
            result.push({
                label: "Edit in Shopify",
                tabId: ProductTabIds.editInShopify,
                redirect: shopifyAdminUrl,
                openInNewTab: true,
            });
        }

        return result;
    }, [productData?.shopifyProductId, productData?.store?.id, productData?.store?.name, productData?.mainSku, shopifyAdminUrl, productListingId, hasPayoutRuleAssignments, hasCostMetadata, onCostMetadataDataLoaded, onPayoutRuleDataLoaded]);

    const tabs: TabInfo[] = getTabs();

    // Resolve selected tab from URL after product data loads
    useEffect(() => {
        if (productLoading) {
            return;
        }
        setSelectedTabIndex(resolveSelectedTabIndex(tabId));
    }, [productLoading, resolveSelectedTabIndex, tabId]);

    // Sync URL with the selected tab
    useEffect(() => {
        if (selectedTabIndex === null) {
            return;
        }
        NavigationHelper.pushPathname(
            history,
            generatePath(PRODUCT_ROUTE_PATH, {
                productListingId,
                tabId: tabs[selectedTabIndex]?.tabId,
            })
        );
    }, [history, selectedTabIndex, productListingId, tabs]);

    if (productLoading || selectedTabIndex === null) {
        return <CenteredSpinner/>;
    }

    if (productError || !productData) {
        return (
            <EmptyState
                title="Failed to load product"
                subtitle="Refresh the page to try again"
            >
                <ErrorIcon size={60}/>
            </EmptyState>
        );
    }

    return (
        <MultiTabView
            header={productData.name}
            tabs={tabs}
            selectedTabIndex={selectedTabIndex}
            onTabSelected={setSelectedTabIndex}
            fullPathFormat={PRODUCT_ROUTE_PATH}
            backButtonHeader="All Products"
            backButtonOnClick={() => history.push("/products")}
        />
    );
};

export const MultiTabProductPage = () => {
    return (
        <RampartAuthorized requiredGroups={["juniper.self-serve.admin"]}>
            <MultiTabProductPageInternal />
        </RampartAuthorized>
    );
};
